import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  CONNECTOR_LABEL,
  SIGNAL_LEVEL_LABEL,
  SIGNAL_TYPE_LABEL,
  PHANTOM_POWER_LABEL,
} from "../constants/signalLabels";

const ASPECT_OPTIONS = {
  connector: CONNECTOR_LABEL,
  signal_type: SIGNAL_TYPE_LABEL,
  signal_level: SIGNAL_LEVEL_LABEL,
  phantom_power: PHANTOM_POWER_LABEL,
};

const ASPECT_LABELS = {
  connector: "Connector",
  signal_type: "Signal Type",
  signal_level: "Signal Level",
  phantom_power: "Phantom Power",
};

function BoundaryLabel({ label, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(label);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== label) {
      onCommit(trimmed);
    }
    setEditing(false);
  }, [editValue, label, onCommit]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        commit();
      } else if (e.key === "Escape") {
        setEditValue(label);
        setEditing(false);
      }
    },
    [commit, label],
  );

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="signals-panel-item-label-input"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <div
      className="signals-panel-item-label"
      onDoubleClick={() => {
        setEditValue(label);
        setEditing(true);
      }}
      title="Double-click to edit"
    >
      {label}
    </div>
  );
}

const INPUT_EXCLUDE = {
  connector: new Set(["xlr_combo"]),
  signal_level: new Set(["mic_line"]),
};

function AspectSelect({ field, value, onChange, direction }) {
  const options = ASPECT_OPTIONS[field];
  const label = ASPECT_LABELS[field];
  const exclude =
    direction === "input" ? INPUT_EXCLUDE[field] : null;

  return (
    <div className="signals-panel-aspect">
      <label className="signals-panel-aspect-label">{label}</label>
      <select
        className="signals-panel-aspect-select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        aria-label={label}
      >
        <option value="">—</option>
        {Object.entries(options)
          .filter(([key]) => !exclude || !exclude.has(key))
          .map(([key, displayLabel]) => (
            <option key={key} value={key}>
              {displayLabel}
            </option>
          ))}
      </select>
    </div>
  );
}

function BoundaryItem({ node, updateBoundary, selected, onSelect }) {
  const { boundary } = node.data;
  const itemRef = useRef(null);

  useEffect(() => {
    if (selected && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  const handleLabelCommit = useCallback(
    (trimmed) => {
      updateBoundary(node.id, { label: trimmed });
    },
    [node.id, updateBoundary],
  );

  const handleAspectChange = useCallback(
    (field, value) => {
      updateBoundary(node.id, { [field]: value });
    },
    [node.id, updateBoundary],
  );

  return (
    <div
      ref={itemRef}
      className={`signals-panel-item signals-panel-item--${boundary.direction}${selected ? " signals-panel-item--selected" : ""}`}
      onClick={() => onSelect(node.id)}
    >
      <BoundaryLabel label={boundary.label} onCommit={handleLabelCommit} />
      <div className="signals-panel-aspects">
        {["connector", "signal_type", "signal_level", "phantom_power"].map(
          (field) => (
            <AspectSelect
              key={field}
              field={field}
              value={boundary[field]}
              onChange={(val) => handleAspectChange(field, val)}
              direction={boundary.direction}
            />
          ),
        )}
      </div>
    </div>
  );
}

function onBoundaryDragStart(event, direction) {
  event.dataTransfer.setData(
    "application/json",
    JSON.stringify({
      boundary: {
        direction,
        label: direction === "output" ? "Output" : "Input",
      },
    }),
  );
  event.dataTransfer.effectAllowed = "move";
}

export default function SignalsPanel({ nodes, updateBoundary, selectedBoundaryId, onSelectBoundary }) {
  const boundaryNodes = nodes.filter((n) => n.type === "signalBoundary");
  const outputs = boundaryNodes.filter(
    (n) => n.data.boundary.direction === "output",
  );
  const inputs = boundaryNodes.filter(
    (n) => n.data.boundary.direction === "input",
  );

  return (
    <div className="signals-panel">
      <div className="signals-panel-boundaries">
        <div className="signals-panel-boundaries-items">
          <div
            className="signals-panel-boundary-drag signals-panel-boundary-drag--output"
            draggable
            onDragStart={(e) => onBoundaryDragStart(e, "output")}
          >
            Output
            <div className="signals-panel-boundary-grip" aria-hidden="true" title="Drag to add">&#x2630;</div>
          </div>
          <div
            className="signals-panel-boundary-drag signals-panel-boundary-drag--input"
            draggable
            onDragStart={(e) => onBoundaryDragStart(e, "input")}
          >
            Input
            <div className="signals-panel-boundary-grip" aria-hidden="true" title="Drag to add">&#x2630;</div>
          </div>
        </div>
      </div>

      {boundaryNodes.length === 0 && (
        <div className="signals-panel-empty">
          No signals on the board yet. Drag an Output or Input above to get
          started.
        </div>
      )}

      {outputs.length > 0 && (
        <>
          <div className="signals-panel-section-title">Outputs</div>
          {outputs.map((node) => (
            <BoundaryItem
              key={node.id}
              node={node}
              updateBoundary={updateBoundary}
              selected={node.id === selectedBoundaryId}
              onSelect={onSelectBoundary}
            />
          ))}
        </>
      )}

      {inputs.length > 0 && (
        <>
          <div className="signals-panel-section-title">Inputs</div>
          {inputs.map((node) => (
            <BoundaryItem
              key={node.id}
              node={node}
              updateBoundary={updateBoundary}
              selected={node.id === selectedBoundaryId}
              onSelect={onSelectBoundary}
            />
          ))}
        </>
      )}
    </div>
  );
}
